import { z } from "zod";

/**
 * Available template components in the system
 *
 * When adding a new template:
 * 1. Create the template component in templates/ directory
 * 2. Export it from templates/index.ts
 * 3. Add its name to this enum
 */
export enum AvailableTemplates {
  DEFAULT = "DefaultTemplate"
  // EXECUTIVE = "ExecutiveTemplate",    // Future template
  // COMPLETE = "CompleteTemplate",      // Future template
  // SIMPLIFIED = "SimplifiedTemplate",  // Future template
}

/**
 * Allowed image MIME types for template backgrounds
 */
export const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
export type AllowedImageType = (typeof ALLOWED_IMAGE_TYPES)[number];

/**
 * Maximum image file size in bytes (2MB)
 */
export const MAX_IMAGE_SIZE_BYTES = 2 * 1024 * 1024;

/**
 * Zod schema for template image validation
 */
export const templateImageSchema = z.object({
  id: z.string().min(1, "ID é obrigatório"),
  nome: z
    .string()
    .min(1, "Nome é obrigatório")
    .max(50, "Nome deve ter no máximo 50 caracteres")
    .regex(/^[\w-]+$/, "Nome deve conter apenas letras, números, underscores e hífens"),
  url: z.string().url("URL inválida"),
  tipo: z.enum(["url", "storage"]),
  storagePath: z.string().optional(),
  createdAt: z.unknown() // Firestore Timestamp
});

/**
 * Schema for adding an image via URL
 */
export const addImageUrlSchema = z.object({
  nome: z
    .string()
    .min(1, "Nome é obrigatório")
    .max(50, "Nome deve ter no máximo 50 caracteres")
    .regex(/^[\w-]+$/, "Nome deve conter apenas letras, números, underscores e hífens"),
  url: z.string().url("URL inválida")
});

export type AddImageUrlInput = z.infer<typeof addImageUrlSchema>;

/**
 * Zod schema for template form validation
 *
 * Validates all required fields for creating/editing report templates
 */
export const templateFormSchema = z.object({
  /**
   * Template name - user-friendly identifier
   * Min 3 characters, max 100
   */
  nome: z
    .string()
    .min(3, "Nome deve ter no mínimo 3 caracteres")
    .max(100, "Nome deve ter no máximo 100 caracteres")
    .trim(),

  /**
   * Template description - explains purpose and use case
   * Min 10 characters, max 500
   */
  descricao: z
    .string()
    .min(10, "Descrição deve ter no mínimo 10 caracteres")
    .max(500, "Descrição deve ter no máximo 500 caracteres")
    .trim(),

  /**
   * Component path - React component name to render
   * Must match AvailableTemplates enum
   */
  componentPath: z.nativeEnum(AvailableTemplates, {
    errorMap: () => ({ message: "Template inválido. Selecione um template válido." })
  }),

  /**
   * Active status - whether template is available for use
   * Inactive templates are hidden from template selector
   */
  ativo: z.boolean().default(true),

  /**
   * Accessible to guest users flag
   * When true, users with "convidada" role can access this template
   * Default: false (templates are not accessible to guests by default)
   */
  acessivelConvidada: z.boolean().default(false).optional(),

  /**
   * Optional configuration object for template-specific settings
   * Structure depends on the template component
   */
  configuracao: z
    .object({
      incluirGraficos: z.boolean().optional(),
      incluirTabelas: z.boolean().optional(),
      incluirAnaliseDetalhada: z.boolean().optional()
    })
    .optional(),

  /**
   * HTML template content for custom templates
   * Optional - if provided, enables custom HTML editor
   * Max 50KB (51200 characters) for performance
   * Supports variable substitution: {{variavel}}
   */
  htmlTemplate: z.string().max(51_200, "Template HTML deve ter no máximo 50KB").trim().optional()
});

/**
 * TypeScript type inferred from Zod schema
 * Use this for type-safe form handling
 */
export type TemplateFormData = z.infer<typeof templateFormSchema>;

/**
 * Default form values for creating new templates
 */
export const defaultTemplateFormValues: TemplateFormData = {
  nome: "",
  descricao: "",
  componentPath: AvailableTemplates.DEFAULT,
  ativo: true,
  acessivelConvidada: false,
  configuracao: {
    incluirGraficos: true,
    incluirTabelas: true,
    incluirAnaliseDetalhada: true
  },
  htmlTemplate: undefined
};

/**
 * Convert existing template to form data
 * Useful for edit mode
 * @param template Existing template from Firestore
 * @returns Form data ready for editing
 */
/**
 * Input type for templateToFormData - accepts Firestore data with unknown shape
 */
interface TemplateFormInput {
  nome?: string;
  descricao?: string;
  componentPath?: string;
  ativo?: boolean;
  acessivelConvidada?: boolean;
  configuracao?: {
    incluirGraficos?: boolean;
    incluirTabelas?: boolean;
    incluirAnaliseDetalhada?: boolean;
  };
  htmlTemplate?: string;
}

/**
 *
 * @param template
 */
export function templateToFormData(template: TemplateFormInput | null | undefined): TemplateFormData {
  if (!template) {
    return defaultTemplateFormValues;
  }

  return {
    nome: template.nome || "",
    descricao: template.descricao || "",
    componentPath: (template.componentPath as AvailableTemplates) || AvailableTemplates.DEFAULT,
    ativo: template.ativo ?? true,
    acessivelConvidada: template.acessivelConvidada ?? false,
    configuracao: template.configuracao || defaultTemplateFormValues.configuracao,
    htmlTemplate: template.htmlTemplate || undefined
  };
}
